<script setup lang="ts">
    import { computed, onMounted, nextTick, shallowReactive, watch, ref, type Ref } from 'vue'
    import { useRoute } from 'vue-router'

    import PortfolioNavigation from '@common/Components/PortfolioNavigation.vue'
    import Navigation from '@common/Navigation.vue'
    import ProjectsGrid from '@Quest/Projects/Components/ProjectsGrid.vue'

    import { ProjectsGridViewModel } from '@Quest/Projects/Components/ProjectsGrid'
    import { MyPortfolioProjectsPageReadModel, GridProjectReadModel } from '@Quest/Projects/readmodels'

    import { useMyPortfolioProjectsPageQueryV1 } from '@Quest/Projects/queries-vue-composables'
    import { useSaveViewOwnerProjectsPageCommandV1 } from '@Quest/Portfolios/commands-vue-composables'

    const route = useRoute()

    const workspaceId = computed(() => Number(route.params.workspaceId ?? 0))
    const tchProjectId = computed(() => Number(route.params.tchProjectId ?? 0))

    // Query
    const {
        runMyPortfolioProjectsPageQueryV1,
        modelMyPortfolioProjectsPageQueryV1
    } = useMyPortfolioProjectsPageQueryV1(workspaceId, tchProjectId)

    const rm = modelMyPortfolioProjectsPageQueryV1 as Ref<MyPortfolioProjectsPageReadModel | null>
    const projects = computed(() => rm.value?.Projects ?? [])
    const portfolioId = computed(() => rm.value?.PortfolioId ?? '')
    const portfolioName = computed(() => rm.value?.PortfolioName ?? null)
    const isWorkspaceMember = computed(() => rm.value?.IsWorkspaceMember ?? false)

    // Command
    const {
        invokeSaveViewOwnerProjectsPageCommandV1,
        succeedSaveViewOwnerProjectsPageCommandV1
    } = useSaveViewOwnerProjectsPageCommandV1(workspaceId, portfolioId)

    // Stable ViewModel instance
    const grid = shallowReactive(new ProjectsGridViewModel())
    //const selectedIds = ref<string[]>([])


    // Grid handlers
    function handleClearGridFilters()
    {
        grid.clearFilters()
    }

    function handleExportGrid()
    {
        grid.exportGrid()
    }

    function handleShowGridColumnsEditor()
    {
        grid.showGridColumnsEditor()
    }

    function handleSaveViewSettings()
    {
        const gridJson = grid.getJson()

        invokeSaveViewOwnerProjectsPageCommandV1({
            grid: gridJson
        })
    }

    // Query lifecycle
    onMounted(() =>
    {
        document.title = "Project owner | TransparentChoice"
        runMyPortfolioProjectsPageQueryV1()
    })

    // Replace onSetModel logic
    watch(rm, (current, previous) =>
    {
        if (current && !previous)
        {
            nextTick(() =>
            {
                //const getSelectedIds = (e: any) =>
                //{
                //    const selectedRows = e.sender.select()
                //    const ids: string[] = []

                //    for (const r of selectedRows)
                //    {
                //        const dataItem = e.sender.dataItem(r)
                //        ids.push(dataItem[current.Grid.IdField] as string)
                //    }

                //    selectedIds.value = [...new Set(ids)]
                //}

                grid.renderGrid(
                    current.Grid.IdField,
                    current.Grid.Fields,
                    current.Grid.DefaultFieldIds,
                    current.Grid.Options,
                    //getSelectedIds
                )

                grid.setProjects(current.Projects.map(p => p.Properties))
            })
        }
    })

    // Command success watcher
    watch(succeedSaveViewOwnerProjectsPageCommandV1, (current, previous) =>
    {
        if (current && !previous)
        {
            alert("Saved")
        }
    })
</script>

<template>
    <div>
        <PortfolioNavigation v-if="isWorkspaceMember" :workspaceId="workspaceId" :projectId="tchProjectId" />
        <Navigation v-else />

        <div class="container-fluid" style="margin-top: 80px; padding-top: 20px;">
            <div class="row">
                <div class="col-lg-6">
                    <h6 class="text-muted mt-2">{{portfolioName}}</h6>
                    <h1 style="font-size: 2em; font-weight: 300; color: #33475b; margin: 0;">My projects</h1>
                </div>
                <div class="col-lg-6 col-lg-6 text-right align-self-end">
                    <button role="button" class="btn btn-outline-secondary" style="padding: 0.7em 2em;" @click="handleSaveViewSettings">Save</button>
                </div>
            </div>

            <hr style="border-color: #bbb; margin: 20px 0 60px;" />


            <div class="toolbar">
                <div>
                    <!--<button type="button" class="btn btn-outline-secondary">Action ({{selectedIds.length}})</button>-->
                </div>
                <div>
                    <button role="button" class="btn btn-outline-secondary mr-1" @click="handleShowGridColumnsEditor">Edit columns</button>

                    <div class="btn-group mr-1">
                        <button type="button" class="btn btn-outline-secondary dropdown-toggle" data-toggle="dropdown">
                            Actions
                        </button>
                        <div class="dropdown-menu dropdown-menu-right">
                            <a class="dropdown-item" @click="handleClearGridFilters">Clear Filters</a>
                        </div>
                    </div>

                    <button type="button" class="btn btn-outline-secondary" @click="handleExportGrid" title="Download to spreadsheet">
                        <i class="fa fa-download fa-lg"></i>
                    </button>
                </div>
            </div>

            <ProjectsGrid :viewModel="grid" />
        </div>
    </div>
</template>

<style scoped>
    .toolbar {
        background-color: #f8f8f8; /* Light gray background for the toolbar */
        padding: 10px 15px; /* Padding around the toolbar */
        border: 1px solid #e4e4e4; /* A light border to give separation */
        display: flex; /* Use flex to give layout flexibility */
        align-items: center; /* Center-align items vertically */
        justify-content: space-between; /* Separate items */
    }
</style>